import { useState } from "react";
import { Modal, CircularProgress } from "@mui/material";
import { IoClose } from "react-icons/io5";
import { mySpaceUploadFile } from "../../utils/api/mySpaceApi";
import { useDispatch } from "react-redux";
import { pushAlert } from "../../utils/alertSlice";
import { getFileExtension, removeFileExtension, truncateString } from "../../utils/utilityFunctions/stringUtility";
import { ReactComponent as DownloadIcon } from "../../assets/images/downloadIcon.svg";
import Button from "../UniversalComponents/DefaultButton";

const FileUploadModal = ({ open, onClose, currentFolderId = null, onSuccess = () => {}, onFailure = () => {} }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fileError, setFileError] = useState("");
  const dispatch = useDispatch();

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const maxSizeInBytes = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSizeInBytes) {
        setFileError("File size exceeds 10MB limit.");
        setSelectedFile(null);
      } else {
        setSelectedFile(file);
        setFileError("");
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await mySpaceUploadFile({
        body: formData,
        query: currentFolderId ? { parentId: currentFolderId } : undefined,
      });

      if (response?.success === true) {
        dispatch(pushAlert({ type: "success", message: response?.message, visible: true }));
        onClose();
        onSuccess(currentFolderId);
      } else {
        setFileError(response?.message || "Upload failed.");
        dispatch(pushAlert({ type: "danger", message: response?.message, visible: true }));
        onFailure();
      }
    } catch (error) {
      dispatch(pushAlert({ type: "danger", message: error?.message, visible: true }));
      setFileError(error?.message || "Upload failed.");
      onFailure();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} className="flex items-center justify-center ">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[500px] outline-none">
        {/* Header */}
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">File Upload</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <IoClose size={24} />
          </button>
        </div>

        {/* File Input */}
        <div className="px-6 py-4">
          <label className="block text-sm font-medium  mb-2 text-[#434852] text-12">Upload File</label>
          <div className="relative border border-[#EAE2E2] rounded-md h-40 flex flex-col items-center justify-center text-gray-500 text-sm">
            <input
              type="file"
              onChange={handleFileChange}
              disabled={loading}
              className="absolute w-full h-full opacity-0 cursor-pointer z-10"
            />
            <div className="z-0 text-center px-3">
              <p className="text-[#66666699]">Drop files here or click to select from the device</p>
              <div className="mt-2 inline-block px-4 py-1.5 border rounded-md bg-gray-100 text-Table  hover:bg-gray-200 transition">
                Upload from this device <DownloadIcon className="inline ml-1 w-4 h-4" />
              </div>
              {selectedFile?.name && (
                <p className="mt-2 text-blue-600 text-sm">
                  {removeFileExtension(truncateString({ str: selectedFile.name, maxLength: 30 })) +
                    "." +
                    getFileExtension(selectedFile.name)}
                </p>
              )}
            </div>
          </div>

          {fileError && <p className="text-sm text-red-600 mt-2">{fileError}</p>}
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end gap-2 px-6 py-4">
          <Button onClick={onClose} disabled={loading} variant="secondary">
            Cancel
          </Button>
          <Button onClick={handleUpload} disabled={!selectedFile || loading} variant="primary">
            {loading ? (
              <div className="flex items-center gap-2">
                <CircularProgress size={12} color="inherit" />
                Uploading...
              </div>
            ) : (
              "Upload"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default FileUploadModal;
